<template>
  <article
    class="recipe-card"
    @click="$emit('select-recipe', recipe.id)"
    @keydown.enter="$emit('select-recipe', recipe.id)"
    role="button"
    tabindex="0"
    :aria-label="recipe.title"
  >
    <div class="card-image-wrapper">
      <img
        :src="recipe.imageUrl"
        :alt="recipe.title"
        class="card-image"
        loading="lazy"
        @error="handleImageError"
      />
      <span class="category-badge">
        {{ recipe.categoryIcon }} {{ recipe.categoryName }}
      </span>
    </div>

    <div class="card-body">
      <div class="card-meta">
        <span class="meta-item">⏱️ {{ recipe.totalTime || recipe.cookTime }}</span>
        <span class="meta-item">👥 {{ recipe.servings }} port</span>
        <span class="meta-item">📊 {{ recipe.difficulty }}</span>
      </div>

      <h3 class="card-title">{{ recipe.title }}</h3>
      <p class="card-description">{{ recipe.description }}</p>

      <div class="card-footer">
        <span class="view-recipe-text">Se hela receptet</span>
        <span class="arrow-icon">→</span>
      </div>
    </div>
  </article>
</template>

<script>
export default {
  name: 'RecipeCard',
  props: {
    recipe: {
      type: Object,
      required: true
    }
  },
  emits: ['select-recipe'],
  methods: {
    handleImageError(event) {
      event.target.style.display = 'none';
    }
  }
}
</script>

<style scoped>
.recipe-card {
  background-color: var(--color-card-bg);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  touch-action: manipulation;
  outline: none;
}

.recipe-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-md);
  border-color: rgba(230, 81, 0, 0.3);
}

.recipe-card:active {
  transform: scale(0.99);
}

.recipe-card:focus-visible {
  box-shadow: 0 0 0 3px rgba(230, 81, 0, 0.4);
}

.card-image-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  min-height: 180px;
  background-color: #f0eae1;
  overflow: hidden;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.35s ease;
}

.recipe-card:hover .card-image {
  transform: scale(1.04);
}

.category-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(6px);
  padding: 4px 10px;
  border-radius: var(--radius-lg);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--color-text-main);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.card-body {
  padding: 18px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.card-meta {
  display: flex;
  gap: 12px;
  margin-bottom: 10px;
  font-size: 0.82rem;
  color: var(--color-text-muted);
  flex-wrap: wrap;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.card-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--color-text-main);
  margin-bottom: 8px;
  line-height: 1.3;
}

.card-description {
  font-size: 0.9rem;
  color: var(--color-text-muted);
  line-height: 1.5;
  margin-bottom: 14px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex-grow: 1;
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid var(--color-border);
  color: var(--color-primary);
  font-weight: 600;
  font-size: 0.92rem;
}

.arrow-icon {
  transition: transform 0.2s ease;
}

.recipe-card:hover .arrow-icon {
  transform: translateX(4px);
}

@media (max-width: 640px) {
  .card-body {
    padding: 14px;
  }
  .card-title {
    font-size: 1.1rem;
  }
  .card-description {
    font-size: 0.86rem;
    margin-bottom: 10px;
  }
  .card-meta {
    gap: 8px;
    font-size: 0.78rem;
  }
}
</style>
